<template>
  <Component :is="tag" ref="counter">
    <span class="sr-only">{{ value }}</span>
    <span aria-hidden="true">{{ displayValue }}</span>
  </Component>
</template>

<script setup lang="ts">
import { ScrollTrigger } from 'gsap/ScrollTrigger'

const props = defineProps({
  value: { // The value can be a string with a prefix, number, and suffix. Example: "Total: 1234.56 units"
    type: String,
    required: true,
  },
  duration: {
    type: Number,
    default: 2000, // Duration in milliseconds
  },
  tag: {
    type: String,
    default: 'span', // Default tag to render the counter
  },
  addSpacesToNumber: {
    type: Boolean,
    default: true, // Whether to add spaces as thousand separators
  },
})

const current = ref(0)
const counter = ref<HTMLElement | null>(null)

// Parse target string to extract prefix, number, and suffix
const parsed = computed(() => {
  // Match: (prefix)(number)(suffix), where number can be negative/decimal
  const match = props.value.match(/^([^0-9-]*)(-?\d*\.?\d+)?(.*)$/)
  if (!match || !match[2]) {
    return {
      prefix: props.value,
      number: 0,
      suffix: '',
      hasNumber: false,
    }
  }
  return {
    prefix: match[1] || '',
    number: parseFloat(match[2]),
    suffix: match[3] || '',
    hasNumber: true,
  }
})

// Helper function to format number with space as thousand separator
const formatNumber = (num: number): string => {
  const isNegative = num < 0
  const absNum = Math.abs(num)
  // Preserve decimal places from original number
  const decimalMatch = parsed.value.hasNumber
    ? parsed.value.number.toString().match(/(\.\d+)?$/)
    : null
  const decimalPlaces = decimalMatch && decimalMatch[1] ? decimalMatch[1].length - 1 : 0
  const numStr = decimalPlaces > 0 ? absNum.toFixed(decimalPlaces) : Math.floor(absNum).toString()
  let formatted = ''
  const [integer, decimal] = numStr.split('.')
  // Format integer part with spaces
  for (let i = integer.length - 1, count = 0; i >= 0; i--) {
    formatted = integer[i] + formatted
    count++
    if (props.addSpacesToNumber && count % 3 === 0 && i > 0) {
      formatted = ' ' + formatted
    }
  }
  // Add decimal part if present
  formatted = decimal ? `${formatted}.${decimal}` : formatted
  return isNegative ? '-' + formatted : formatted
}

// Reconstruct display value with formatted number
const displayValue = computed(() => {
  if (!parsed.value.hasNumber) {
    return props.value // No number, return original
  }
  return `${parsed.value.prefix}${formatNumber(current.value)}${parsed.value.suffix}`
})

const animateCounter = () => {
  if (!parsed.value.hasNumber) return // No number to animate

  const start = performance.now()
  const targetNum = parsed.value.number

  const step = (timestamp: number) => {
    const progress = Math.min((timestamp - start) / props.duration, 1)
    current.value = progress * targetNum
    if (progress < 1) {
      requestAnimationFrame(step)
    } else {
      current.value = targetNum // End at exact value
    }
  }
  requestAnimationFrame(step)
}

let scrollTriggerInstance: ScrollTrigger | null = null
onMounted(() => {
  if (!parsed.value.hasNumber || !counter.value) return // No number or element

  // Create ScrollTrigger to trigger animateCounter when in view
  scrollTriggerInstance = ScrollTrigger.create({
    trigger: counter.value,
    start: 'top 80%', // Start when top of counter is 80% from top of viewport
    onEnter: () => {
      animateCounter()
    },
    once: true, // Run only once when entering viewport
  })
})

onUnmounted(() => {
  scrollTriggerInstance?.kill()
})
</script>
```
